import { ExploreOffSharp } from '@mui/icons-material';
import axios from 'axios';
import detectPlatform from '../utils/detectPlatform';
import decodeToken from '../lib/decodeToken';
import { detectCheckoutCurrency } from '../lib/checkoutCurrency';
import { isPythonBackend, fetchPythonAgentDetail, usesVobiz, isAppSumoUser } from '../utils/apiBackend';
// activeContext imports only decodeToken, so this does not create a cycle back
// into apiStore — setContactOwnerId is passed INTO it as an argument for that
// exact reason.
import { getActiveOwnerId, getAgentOwnerId } from '../lib/activeContext';
import { setVariantFromServer, EXPERIMENTS } from '../lib/experiments';
import installNgrokSkipWarning from '../lib/ngrokSkipWarning';
// Centralized API base URL here
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:2512/api';
export const token = localStorage.getItem('token') || '';
// Create axios instance
// NOTE: no Authorization default here on purpose. Axios merges instance
// defaults into the request config BEFORE interceptors run, so a default
// pinned to the module-load token would always win over the interceptor
// below — and `Bearer ` (empty token, tab opened logged-out) is a truthy
// string, so it would not even look absent. The interceptor sets a FRESH
// token instead, and any call passing its own Authorization still wins.
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// ── ngrok dev backend: skip the free-tier browser interstitial ───────────────
// Wired here because this is the only place BOTH axios paths are in scope: the
// `api` instance above, and the global `axios` used directly with absolute
// `${API_BASE_URL}/...` URLs throughout this file. Interceptors on the global
// axios do NOT apply to instances made with axios.create(), so the instance has
// to be handed over explicitly.
//
// Self-disabling: no-ops unless REACT_APP_API_BASE_URL points at an ngrok host.
// Production is unaffected, so this line is safe to commit and safe to leave.
installNgrokSkipWarning(api);

// ── Multi-owner contacts: X-Owner-Id header ──────────────────────────────────
// Contacts are per-owner. An admin team member viewing an owner's contacts sets
// the selected owner here; the backend (resolveContactOwner) scopes to that
// owner after verifying admin access. Owners / support clear it → backend uses
// the caller's own account.
//
// We set it directly on the axios instance's default headers — the canonical,
// reliable way in axios 1.x (a request interceptor mutating AxiosHeaders via
// bracket-assignment is flaky and was silently dropping the header, so every
// company resolved to the caller's own id). Only /contact-routes reads this
// header server-side, so carrying it on other requests is harmless.
let _contactOwnerId = null;
export const setContactOwnerId = (id) => {
  _contactOwnerId = id || null;
  if (id) {
    api.defaults.headers.common['X-Owner-Id'] = id;
  } else {
    delete api.defaults.headers.common['X-Owner-Id'];
  }
};
export const getContactOwnerId = () => _contactOwnerId;

// Cross-environment safety net: a custom header (X-Owner-Id) can be stripped by
// CORS on a different-origin deployment (e.g. QA/prod), which silently scopes
// contacts back to the caller's own account. So ALSO send the owner id as a
// query param on every contact-routes request — query params are immune to the
// CORS custom-header preflight, and the backend reads req.query.ownerId too.
api.interceptors.request.use((config) => {
  // Always attach the CURRENT token. The instance default below was captured
  // once at module load (`export const token`), so any call that relies on it
  // sends whatever was in localStorage when the bundle loaded — empty for a
  // tab opened logged-out, then stale after a soft login with no reload. That
  // was harmless while these routes had no auth; now that they do, it is a
  // 401. Calls that pass their own Authorization keep it.
  const fresh = localStorage.getItem('token');
  if (fresh) {
    // Use AxiosHeaders.set() when present. This file already learned once
    // that bracket-assigning onto AxiosHeaders inside an interceptor is
    // flaky and silently drops the header (see the X-Owner-Id note above),
    // so don't repeat it. `has()` keeps a caller's explicit Authorization.
    const h = config.headers;
    if (h && typeof h.set === 'function') {
      if (!h.has('Authorization')) h.set('Authorization', `Bearer ${fresh}`);
    } else {
      config.headers = { ...(h || {}) };
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${fresh}`;
      }
    }
  }

  if (_contactOwnerId && (config.url || '').includes('contact-routes')) {
    config.params = { ...(config.params || {}), ownerId: _contactOwnerId };
  }

  // Pounce: scope to the owner of the agent that is OPEN, not just the active
  // context. On a legacy multi-company session those differ, and the default
  // header would ask for another company's Pounce data. The backend
  // (resolveOwnerScope) still verifies membership; an account owner's own id
  // is ignored there, so this is a no-op for them.
  if ((config.url || '').includes('/pounce/')) {
    const agentOwnerId = getAgentOwnerId();
    const h = config.headers;
    if (agentOwnerId && h && typeof h.set === 'function') h.set('X-Owner-Id', agentOwnerId);
  }
  return config;
});

const userId = sessionStorage.getItem('userId');

// ========== Auth APIs ==========
// Get all knowledge bases
export const LoginWithEmailOTP = async (email, environmentInfo, utm_data, recaptchaToken) => {
  const res = await api.post('/auth/LoginWithEmailOTP', {
    email,
    environmentInfo,
    utm_data,
    recaptchaToken,
    // Tells the backend which surface the user is signing in from
    // ('android' / 'ios' / 'web') so it can persist on EndUser.platform.
    platform: detectPlatform()
  });
  return res;
};

// ========== Shopify integration ==========
// Flow 1 (App Store new user): after signup, hand the buffered handoff token to
// the backend, which writes the encrypted integration_configs row for the now-
// authenticated merchant. Reads a FRESH token from localStorage — the module-level
// `api` instance captured an older/empty token at import time, so we can't use it.
// export const finalizeShopifyConnection = async (handoffToken) => {
//   const freshToken = localStorage.getItem('token') || '';
//   return axios.post(
//     `${API_BASE_URL}/integration/shopify/finalize`,
//     { shopify_handoff_token: handoffToken },
//     { headers: { Authorization: `Bearer ${freshToken}` } }
//   );
// };

export const verifyEmailOTP = async (email, otp, customer_id) => {
  const customerRes = await fetch(`${API_BASE_URL}/customer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  });

  const customerData = await customerRes.json();
  let customerId = customerData.customerId;
  const res = await api.post('/auth/verifyEmailOTP', {
    email,
    otp,
    customerId,
    // Backfill platform on verify too — covers users created before this
    // field existed.
    platform: detectPlatform()
  });
  return res;
};

export const verifyOrCreateUser = async (email, otp, customerIdOverride, source) => {
  // 4th arg `source` (e.g. "facebook") is forwarded to LoginWithEmailOTP so
  // the backend stamps it on EndUser.source AND mirrors it as the new
  // SalesLead's leadSource. Existing 2/3-arg callers keep working because
  // both extra args are optional.
  const res1 = await api.post('/auth/LoginWithEmailOTP', {
    email,
    fullOtp: otp,
    ...(source ? { source } : {}),
  });

  const customerRes = await fetch(`${API_BASE_URL}/customer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  });

  const customerData = await customerRes.json();
  let customerId = customerData.customerId;
  const res = await api.post('/auth/verifyEmailOTP', {
    email,
    otp,
    customerId
  });
  return { res1, res };
};
// Calendar connections are AGENT-WISE, so "is the calendar connected?" is asked
// for one agent. `agentId` defaults to the agent the user is working on
// (SelectAgentId) — every agent screen and wizard that calls this means that
// agent. Pass `null` explicitly for the account-wide answer ("connected on any
// agent"). Without an agent the backend also answers account-wide.
export const calenderCheck = async (
  userId,
  agentId = sessionStorage.getItem("SelectAgentId")
) => {
  const res = await axios.post(
    `${API_BASE_URL}/calender-check`,
    { userId, ...(agentId ? { agentId } : {}) } // 👈 SEND BODY
  );
  return res;
};

// ── Google Calendar for ONE agent (owner or an admin of that owner) ──────────
// Connections are agent-wise: connecting links that agent only.
// Authenticated: these go through `api`, whose interceptor attaches the current
// token — the backend checks the caller against the agent's owner / admins.
// status → { role, agentLinked, agentConnection, ownerOnApple, canConnect,
//            canDisconnect }
export const getAgentCalendarStatus = async (agentId) =>
  (await api.post('/agent-calendar/status', { agentId })).data;
// Google consent URL to connect an account to this agent only.
export const connectAgentCalendar = async (payload) =>
  (await api.post('/agent-calendar/connect', payload)).data;
// Remove the agent's own connection.
export const disconnectAgentCalendar = async (agentId) =>
  (await api.post('/agent-calendar/disconnect', { agentId })).data;

export const getRetellVoices = async (token) => {
  const res = await api.get('/agent/list-voices', {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

// ========== Forwarding test call (Telnyx) ==========
// Place a call from the agent's Telnyx number to the owner's number; if
// forwarding is live it routes back to the agent (Retell) and answers.
export const startForwardingTestCall = async (fromNumber, toNumber, simulate = false) => {
  const authToken = localStorage.getItem('token') || '';
  const res = await api.post(
    '/telnyx/test-call/start',
    { fromNumber, toNumber, simulate },
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return res.data; // { success, testCallId, status }
};

// Poll a test call's result: 'calling' | 'forwarded' | 'failed' | 'unknown'.
export const getForwardingTestCallStatus = async (testCallId) => {
  const authToken = localStorage.getItem('token') || '';
  const res = await api.get(`/telnyx/test-call/status/${testCallId}`, {
    headers: { Authorization: `Bearer ${authToken}` }
  });
  return res.data; // { success, status }
};

export const createAgent = async (data) => {
  const res = await api.post('/agent/createAgent', data, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return res;
};

export const fetchDashboardDetails = async (userId, token) => {
  let t = token;
  const res = await api.get(`${API_BASE_URL}/agent/getUserAgentsDetails/${userId}`, {
    headers: {
      Authorization: `Bearer ${t}`
    }
  });
  return res.data;
};

export const fetchAgentDetailById = async (data) => {
  // Python flow (rexpt.in / port 3001): agent Python backend me hai — detail
  // Python se lo (Node 404 deta). Response Node-shape me normalized aata hai.
  if (isPythonBackend()) {
    return await fetchPythonAgentDetail(data?.agentId || data?.agent_id || data);
  }
  const res = await api.post('/agent/fetchAgentDetailsById', data, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return res.data;
};
export const getAgentById = async (id) => {
  if (isPythonBackend()) {
    return await fetchPythonAgentDetail(id);
  }
  const res = await api.get(`/agent/getAgentById/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return res.data;
};

export const EndWebCallUpdateAgentMinutesLeft = async (data) => {
  const res = await api.patch(`/agent/updateAgentMinutesLeft`, data, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return res;
};
export const SendScriptToDeveloper = async (data) => {
  const res = await api.post(`/agent/sendScriptToEmail`, data, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return res;
};
// /agent/listAgents now requires auth and is scoped server-side to the caller's
// active context. Both callers below MUST send a fresh token: the axios
// instance's default Authorization header is captured once at module load, so a
// session that loaded the bundle before signing in would send an empty/stale one
// and get a 401 on what used to be an unauthenticated route.
export const listAgents = async () => {
  const res = await api.get(`${API_BASE_URL}/agent/listAgents`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
  });
  return res.data;
};

// Same endpoint as countAgentsbyUserId, but the rows themselves (non-archived).
export const listAgentsbyUserId = async (userId) => {
  const res = await api.get(`${API_BASE_URL}/agent/listAgents?userId=${userId}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
  });
  return Array.isArray(res.data) ? res.data : [];
};

export const countAgentsbyUserId = async (userId) => {
  try {
    const res = await api.get(`${API_BASE_URL}/agent/listAgents?userId=${userId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
    });
    return res.data.length || 0;
  } catch (error) {
    console.error('Error fetching agent count:', error);
    return 0;
  }
};

export const updateProfilePicture = async (userId, data) => {
  const res = await api.patch(`${API_BASE_URL}/endusers/user/update_profile_picture/${userId}`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return res.data;
};
export const updateAgent = async (agentId, updateData) => {
  const res = await api.put(`/agent/updateAgent/${agentId}`, updateData, {
    // Fresh token, not the module-level `token` captured at import. The server
    // now rejects this call outright without a valid one, so a tab that loaded
    // the bundle before sign-in would fail every agent save.
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token') || ''}`
    }
  });
  return res.data;
};
export const updateAgentWidgetDomain = async (id, url) => {
  const data = { url: url };
  const res = await axios.put(`${API_BASE_URL}/agent/updateAgentWidgetDomain/${id}`, data);
  return res.data;
};
export const deleteDomain = async (agentId, domain) => {
  try {
    const res = await fetch(`${API_BASE_URL}/agent/${agentId}/deleteDomain`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ domain })
    });

    const data = await res.json();
    if (res.ok) {
    } else {
      console.error('Delete failed:', data.error);
    }
  } catch (err) {
    console.error('Error deleting domain:', err);
  }
};

export const validateWebsite = async (websiteUrl) => {
  try {
    const res = await api.post('/validate-website', { website: websiteUrl });
    return res.data;
  } catch (error) {
    console.error('Error validating website:', error);
    return { valid: false, reason: 'Error validating website' };
  }
};

export const deleteAgent = async (token, agentId) => {
  try {
    const res = await api.delete(`/agent/deleteAgent/${agentId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data;
  } catch (error) {
    console.error('Error deleting agent:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to delete agent.');
  }
};

export const validateEmail = async (email) => {
  try {
    const res = await api.get(`/validate-email?email=${email}`);
    return res.data;
  } catch (error) {
    console.error('Error validating email:', error);
    return { valid: false, reason: 'Error validating email' };
  }
};

export const getUserAgentMergedDataForAgentUpdate = async (agentId, businessId) => {
  try {
    const res = await api.get(`/agent/getUserAgentMergedDataForAgentUpdate/${agentId}?businessId=${businessId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    return res.data;
  } catch (error) {
    console.error('Error validating email:', error);
    return { valid: false, reason: 'Error validating email' };
  }
};

export const getAllAgentCalls = async (userId) => {
  try {
    // const res = await api.get(`/agent/user/${userId}/agent/calls`, {
    const res = await api.get(`callHistory/agent-call-history/last3months/users/${userId}`, {
      headers: {
        Authorization: `Bearer ${process.env.REACT_APP_API_RETELL_API}`
      }
    });
    return res.data;
  } catch (error) {
    console.error('Error fetching agent calls:', error.response?.data || error.message);
    throw new Error('Failed to fetch agent calls');
  }
};

export const getAgentCallById = async (agentId, callId, start_timestamp) => {
  try {
    // const res = await api.get(`/agent/user/${userId}/agent/calls`, {
    const res = await api.get(`callHistory/getSpecificCallData/call/${agentId}/${callId}?start_timestamp=${start_timestamp}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    return res.data;
  } catch (error) {
    console.error('Error fetching agent calls:', error.response?.data || error.message);
    throw new Error('Failed to fetch agent calls');
  }
};

/*
 * Caught-banner visibility + recap figures. The server resolves everything in
 * the AGENT'S timezone (Monday -> weekly recap, 1st -> monthly recap, both ->
 * two slides); `tz` is only a fallback for agents with no stored timezone.
 */
export const getCaughtBannerStatus = async (agentId) => {
  let tz = '';
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    /* Intl unavailable — backend falls back to its US default */
  }
  // Testing aid ONLY: set localStorage "caughtBanner.debugNow" to an ISO date
  // (e.g. "2026-07-20T10:00:00") to make the backend pretend that is "now" so
  // Monday / 1st-of-month can be exercised on any day. The server ignores it
  // in production, so a stray value can never affect real users.
  let debugNow = '';
  try {
    debugNow = localStorage.getItem('caughtBanner.debugNow') || '';
  } catch {
    /* storage unavailable — no override */
  }
  const params = {};
  if (tz) params.tz = tz;
  if (debugNow) params.debugNow = debugNow;
  const res = await api.get(`/agent/${agentId}/caught-banner`, {
    params: Object.keys(params).length ? params : undefined,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`
    }
  });
  return res.data;
};

// Persist the user's "close" of the caught banner server-side (so it survives
// logout, which wipes localStorage). `date` is the qualifying agent-local day
// from the status response; the banner returns on the next Monday / 1st.
export const dismissCaughtBanner = async (agentId, date) => {
  const res = await api.post(
    `/agent/${agentId}/caught-banner/dismiss`,
    { date },
    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
  );
  return res.data;
};

export const getAgentCalls = async (agentId) => {
  try {
    // const res = await api.get(`/agent/user/${userId}/agent/calls`, {
    const res = await api.get(`/callHistory/agentCalLHistory/${agentId}/last3months`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    return res.data;
  } catch (error) {
    console.error('Error fetching agent calls:', error.response?.data || error.message);
    throw new Error('Failed to fetch agent calls');
  }
};
export async function getUserCallsByMonth(userId, month, year) {
  try {
    const res = await axios.get(`${API_BASE_URL}/callHistory/user/${userId}/calls-by-month`, {
      params: { month, year },
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    return res.data;
  } catch (error) {
    console.error('Error fetching agent calls:', error.response?.data || error.message);
    // throw new Error("Failed to fetch agent calls");
    return error?.response?.data;
  }
}

// Specific agent ka month-wise
export async function getAgentCallsByMonth(agentId, month, year) {
  try {
    const res = await axios.get(`${API_BASE_URL}/callHistory/agent/${agentId}/calls-by-month`, {
      params: { month, year },
      headers: {
        Authorization: `Bearer $${token}`
      }
    });
    return res.data;
  } catch (error) {
    console.error('Error fetching agent calls:', error.response?.data || error.message);
    return error?.response?.data;
    // throw new Error("Failed to fetch agent calls");
  }
}

export const fetchUserDetails = async (id) => {
  const userId = id;
  try {
    const response = await axios.get(`${API_BASE_URL}/endusers/users/${userId}`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    return response;
  } catch (error) {
    console.log(error);
  }
};

export const toggleAgentActivation = async (agentId, deactivate = true) => {
  try {
    const res = await api.patch(`/agent/toggle-activation/${agentId}`, {
      deactivate
    });
    return res.data;
  } catch (error) {
    console.error('Error toggling agent activation:', error.response?.data || error.message);
    throw new Error('Failed to update agent activation status');
  }
};

export const getUserDetails = async (userId, token) => {
  let t = token;
  try {
    const response = await axios.get(`${API_BASE_URL}/endusers/users/${userId}`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${t}`
      }
    });
    // A/B fallback: variant normally JWT me aata hai (login pe set hota hai),
    // par 30-din purane tokens me wo field hai hi nahi. Ye response hamesha DB se
    // aata hai, isliye yahan se cache kar lete hain — warna wo users experiment
    // se bahar reh jaate jab tak dubara login na karein.
    // experimentForced=true tab aata hai jab backend ne EXPERIMENT_GO_LIVE_FLOW
    // env se experiment pin kiya ho — us value ko JWT se bhi upar rakha jaata hai.
    setVariantFromServer(
      EXPERIMENTS.GO_LIVE_FLOW,
      response.data?.experimentVariant,
      response.data?.experimentForced
    );

    // AppSumo tier, cached the same way and for the same reason. It is written
    // to localStorage when a code is redeemed, so it only exists in the browser
    // that did the redeeming — signing in anywhere else would otherwise hide
    // the rewards screens and route the account to the wrong backend. This
    // response comes from the database, so it is the authoritative answer.
    try {
      const tier =
        response.data?.appsumo_tier_level ?? response.data?.user?.appsumo_tier_level;
      if (tier !== null && tier !== undefined) {
        localStorage.setItem("appsumo_tier_level", String(tier));
      } else {
        localStorage.removeItem("appsumo_tier_level");
      }
    } catch (_e) {
      /* storage blocked — the host checks in isPythonBackend still apply */
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching user details:', error);
    throw new Error('Failed to fetch user details');
  }
};

export const updateUserDetails = async (userId, updateData) => {
  try {
    const response = await api.put(`/endusers/users/${userId}`, updateData);
    return response.data;
  } catch (error) {
    console.error('Error updating user details:', error);
    throw new Error('Failed to update user details');
  }
};
export const getUserAgentLimitStatus = async (userId) => {
  try {
    const res = await api.get(`/endusers/user-agent-limit-status?userId=${userId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return res.data;
  } catch (error) {
    console.error('Error toggling agent activation:', error.response?.data || error.message);
    throw new Error('Failed to update agent activation status');
  }
};

export const updateLlm = async (token, llmId, payload) => {
  try {
    const res = await axios.patch(`${API_BASE_URL}/agent/update-llm/${llmId}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return res.data;
  } catch (error) {
    console.error('Error updating LLM:', error.response?.data || error.message);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to update LLM',
      error: error.response?.data || error.message
    };
  }
};

export const fetchLlmDetails = async (llm_id) => {
  const data = { llmId: llm_id };

  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(`${API_BASE_URL}/agent/getLlmDetails`, data, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    return response;
  } catch (error) {
    console.log(error);
  }
};
export const addGeneralTools = async (token, llmId, transfers) => {
  const response = await axios.post(
    `${API_BASE_URL}/agent/addGeneralTools`,
    { llmId, transfers },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    }
  );

  return response.data;
};

export const getBusinessDetailsByBusinessId = async (businessId) => {
  try {
    const res = await api.get(`/businessDetails/by-business-id/${businessId}`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    return res.data;
  } catch (error) {
    console.error('Error fetching business details by business ID:', error.response?.data || error.message);
    throw new Error('Failed to fetch business details');
  }
};

export const updateAgentKnowledgeBaseId = async (agentId, knowledgeBaseId) => {
  try {
    const res = await api.patch(`/agent/${agentId}/knowledge-base`, {
      knowledgeBaseId
    });
    return res.data;
  } catch (error) {
    console.error('Error updating agent knowledge base ID:', error);
    throw new Error('Failed to update knowledge base ID for agent');
  }
};

export const updateEmailSendOtp = async (email, userId) => {
  const res = await api.post('/endusers/updateEmailSendOtp', { email, userId });
  return res;
};

export const updateShowReferralFloatingStatus = async (userId, status) => {
  try {
    const response = await api.patch(`/endusers/updateShowReferralFloatingStatus?userId=${userId}`, { status });
    return response.data;
  } catch (error) {
    console.error('Error updating user details:', error);
    throw new Error('Failed to update user details');
  }
};

export const getUserReferralCodeForDashboard = async (userId) => {
  try {
    const response = await api.get(`/endusers/getUserReferralCodeForDashboard?userId=${userId}`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error updating user details:', error);
    throw new Error('Failed to update user details');
  }
};

export const updateAgentEventId = async (agentId, eventId) => {
  try {
    const res = await api.patch(
      `agent/${agentId}/event-id`,
      {
        eventId
      },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return res.data;
  } catch (error) {
    console.error('Error updating agent eventId:', error.response?.data || error.message);
    throw new Error('Failed to update eventId for agent');
  }
};

// `immediate` tearss the subscription down on the spot even when the customer
// isn't refund-eligible. Delete Agent passes it (the agent row is removed right
// after, so nothing would be left to release the number at period end). Cancel
// Subscription omits it and gets the scheduled-at-period-end path instead.
export const refundAndCancelSubscriptionAgnetApi = async (agentId, minutesLeft, { immediate = false } = {}) => {
  try {
    const res = await axios.post(
      `${API_BASE_URL}/refund`,
      {
        agentId,
        minutesLeft,
        immediate
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    return res.data;
  } catch (error) {
    console.error('Error refunding user', error.response?.data || error.message);
    // Surface the real backend reason (404 "Subscription not found", 400, etc.)
    // instead of a generic message so the UI/logs can show what actually failed.
    const backendMsg = error.response?.data?.error || error.response?.data?.message;
    throw new Error(backendMsg || 'Failed to refund user for agent');
  }
};
export const getEndUserSubscriptions_Billings = async (userId) => {
  try {
    const response = await api.get(`/endusers/fetchEndUserSubscriptions_Billings/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error updating user details:', error);
    throw new Error('Failed to update user details');
  }
};
// ── Saved cards (Edit Profile → Payment Method) ──────────────────────────────
// The backend resolves the Stripe customer from the JWT, so none of these take
// a customerId — passing one would be a way to poke at another account.
const paymentMethodError = (error, fallback) => {
  const backendMsg = error?.response?.data?.error || error?.response?.data?.message;
  return new Error(backendMsg || fallback);
};

// Invoice history read straight from Stripe. Replaces the list that came off
// the `subscriptionsss` mirror, which overwrites its row on every renewal and
// so cannot show payment history. Invoices only — plan and billing dates still
// come from getEndUserSubscriptions_Billings.
export const getBillingInvoices = async () => {
  try {
    const res = await api.get('/billing/invoices');
    return res.data;
  } catch (error) {
    console.error('Error loading invoices:', error.response?.data || error.message);
    throw paymentMethodError(error, 'Failed to load your invoices');
  }
};

export const getPaymentMethods = async () => {
  try {
    const res = await api.get('/payment-methods');
    return res.data;
  } catch (error) {
    console.error('Error loading payment methods:', error.response?.data || error.message);
    throw paymentMethodError(error, 'Failed to load your saved cards');
  }
};

// Upcoming (preview) invoice — the accruing/next bill from Stripe (usage + $5 fee
// + proration, threshold-reset aware). Not part of the invoices list.
export const getUpcomingInvoice = async (subscriptionId) => {
  try {
    const res = await api.get('/billing/upcoming-invoice', {
      params: subscriptionId ? { subscriptionId } : {},
    });
    return res.data;
  } catch (error) {
    console.error('Error loading upcoming invoice:', error.response?.data || error.message);
    return null;
  }
};

// // Returns the SetupIntent client secret that Stripe Elements needs to collect
// // and verify (3DS) a new card without charging it.
// export const createPaymentMethodSetupIntent = async () => {
//   try {
// // Retry a failed renewal. Resolves to { status: 'paid' } or
// // { status: 'requires_action', clientSecret } when the bank wants an OTP —
// // the caller finishes that leg with stripe.confirmCardPayment.
// //
// // A decline comes back as a thrown Error whose message is the bank's own
// // reason; `needsPaymentMethod` / `hostedInvoiceUrl` ride along on it so the UI
// // can point somewhere useful instead of just saying "failed".
export const payInvoice = async (invoiceId) => {
  try {
    const res = await api.post(`/billing/invoices/${invoiceId}/pay`);
    return res.data;
  } catch (error) {
    console.error('Error paying invoice:', error.response?.data || error.message);
    const data = error?.response?.data || {};
    const err = paymentMethodError(error, 'Payment failed. Please try again.');
    err.needsPaymentMethod = !!data.needsPaymentMethod;
    err.hostedInvoiceUrl = data.hostedInvoiceUrl || null;
    throw err;
  }
};

// export const getPaymentMethods = async () => {
//   try {
//     const res = await api.get('/payment-methods');
//     return res.data;
//   } catch (error) {
//     console.error('Error loading payment methods:', error.response?.data || error.message);
//     throw paymentMethodError(error, 'Failed to load your saved cards');
//   }
// };

// Returns the SetupIntent client secret that Stripe Elements needs to collect
// and verify (3DS) a new card without charging it.
export const createPaymentMethodSetupIntent = async () => {
  try {
    const res = await api.post('/payment-methods/setup-intent');
    return res.data;
  } catch (error) {
    console.error('Error creating setup intent:', error.response?.data || error.message);
    throw paymentMethodError(error, 'Failed to start card setup');
  }
};

// ── Pounce ──────────────────────────────────────────────────────────────────
// { [agentId]: { plan, newLeads, callsMade, bookedThisMonth } } — dashboard card.
export const getPounceSummary = async () => {
  const res = await api.get('/pounce/summary');
  return res.data?.data || {};
};

// Pounce agent detail page — agent info, range counts, qualified timestamps,
// live activity. from/to = ISO strings in the viewer's local day boundaries.
export const getPounceAgentOverview = async (agentId, { from, to } = {}) => {
  const res = await api.get(`/pounce/agents/${encodeURIComponent(agentId)}/overview`, {
    params: { from, to },
  });
  return res.data?.data || null;
};

// Onboarding ki plan screen ki choice ("payg" | "subscribe").
export const savePouncePlan = async (agentId, plan) => {
  const res = await api.post('/pounce/plan', { agentId, plan });
  return res.data;
};

// Leads with their call attempts (status, summary, recording), newest first.
export const getPounceLeads = async (params = {}) => {
  const res = await api.get('/pounce/leads', { params });
  return res.data?.data || [];
};


// One lead: attempts, appointment, company + one call's Retell analysis and
// line-wise transcript (attemptId, else the latest answered call).
export const getPounceLeadDetail = async (leadId, attemptId) => {
  const res = await api.get(`/pounce/leads/${encodeURIComponent(leadId)}/detail`, {
    params: attemptId ? { attemptId } : {},
  });
  return res.data?.data || null;
};

// "Call Again" — one extra call through the same gates (DNC, hours, billing).
export const callPounceLeadAgain = async (leadId) => {
  const res = await api.post(`/pounce/leads/${encodeURIComponent(leadId)}/call-again`);
  return res.data;
};

// Pounce on/off. { enabled, heldLeads } — heldLeads = leads that came in while off.
export const getPounceStatus = async () => {
  const res = await api.get('/pounce/status');
  return res.data;
};

export const setPounceEnabled = async (enabled) => {
  const res = await api.post('/pounce/status', { enabled });
  return res.data;
};

// Leads that came in while Pounce was off: "call" them now or "skip" them.
export const resolvePounceHeldLeads = async (action) => {
  const res = await api.post('/pounce/held-leads', { action });
  return res.data;
};

// "Mark Invalid" — bad_number, stops any retries.
export const markPounceLeadInvalid = async (leadId) => {
  const res = await api.post(`/pounce/leads/${encodeURIComponent(leadId)}/invalid`);
  return res.data;
};

// Pounce agent ki booked appointments, lead ke saath. agentId na ho to account
// ke saare Pounce agents ki.
export const getPounceAppointments = async (agentId) => {
  const res = await api.get('/pounce/appointments', {
    params: agentId ? { agentId } : {},
  });
  return res.data?.data || [];

};

// Manual "Add a lead" — Pounce calls it straight away. { phone, name?, inquiry? }
// Qualifying question → uske jawab ka variable naam (backend Gemini se banata
// hai; fail ho to sawaal ke shabdon se). `taken` = baaki sawaalon ke naam.
export const suggestPounceVariable = async (question, taken = []) => {
  const res = await api.post('/pounce/qualifying/suggest-variable', { question, taken });
  return res.data?.variable || "";
};

export const addPounceLead = async (lead) => {
  const res = await api.post('/pounce/leads/manual', lead);
  return res.data;
};

// Lead-source webhook credentials. The raw key comes back only when the
// server can decrypt it; otherwise it is shown once, on create.
// export const getPounceKeys = async () => {
//   const res = await api.sssget('/pounce/keys', { params: { reveal: true } });
//   return res.data?.data || [];
// };

// Pounce Agent Settings - Get current settings (retry gaps, SMS template, business hours)
export const getPounceAgentSettings = async (agentId) => {
  const res = await api.get(`/pounce/agents/${encodeURIComponent(agentId)}/settings`);
  return res.data;
};

// Pounce Agent Settings - Update settings
export const updatePounceAgentSettings = async (agentId, settings) => {
  const res = await api.put(`/pounce/agents/${encodeURIComponent(agentId)}/settings`, settings);
  return res.data;
};

export const createPounceKey = async (label = null) => {
  const res = await api.post('/pounce/keys', { label });
  return res.data;
};
// Pounce API Keys & Webhook connectors
export const getPounceKeys = async (reveal = true) => {
  const res = await api.get('/pounce/keys', {
    params: { reveal: !!reveal },
  });
  return res.data?.data || [];
};

// Special Pounce Pro deal for this account, or null for the standard plans.
// `currency` ("cad" / "usd") prices the deal for the visitor's country.
export const getPouncePlanOptions = async (currency) => {
  const res = await api.get('/pounce/plan-options', { params: { currency } });
  return res.data?.deal || null;
};

// Usage & Billing screen of an Outbound Caller agent: { plan, usage, card }.
// `plan` is null for an agent that is not on the leads plan.
export const getOutboundUsage = async (agentId) => {
  const res = await api.get(`/pounce/agents/${agentId}/usage`);
  return res.data;
};

// One page of an Outbound Caller agent's invoices (plan and extra-lead
// invoices), newest first. Pass the previous page's `nextCursor` as `after`;
// a null `nextCursor` means there are no more.
export const getOutboundInvoices = async (agentId, after) => {
  const res = await api.get(`/pounce/agents/${agentId}/invoices`, { params: { after } });
  return { invoices: res.data?.invoices || [], nextCursor: res.data?.nextCursor || null };
};

// An Outbound Caller subscription this business already paid for that no agent
// uses yet: { onboardingId, plan }, or null. Never throws — on any error the
// setup just shows the plan screen and checkout as usual.
export const findPaidPounceSetup = async (businessId) => {
  try {
    const res = await api.get('/pounce/paid-setup', { params: { businessId } });
    return res.data?.onboardingId ? res.data : null;
  } catch (error) {
    console.error('Error checking for a paid Outbound Caller setup:', error.response?.data || error.message);
    return null;
  }
};

// Start the Pounce trial subscription checkout for the selected business.
export const startPounceCheckout = async ({ businessId, onboardingId, plan, successUrl, cancelUrl }) => {
  try {
    const token = localStorage.getItem('token');
    const decoded = decodeToken(token);
    const userId = decoded?.id || decoded?.userId || sessionStorage.getItem('userId');
    // Same currency the plan screen showed (Canada → CAD, else USD).
    const currency = await detectCheckoutCurrency();

    const res = await api.post('/pounce/checkout', {
      userId,
      businessId,
      onboardingId,
      plan,
      successUrl,
      cancelUrl,
      currency,
    });
    return res.data;
  } catch (error) {
    console.error('Error starting Pounce checkout:', error.response?.data || error.message);
    const message = error?.response?.data?.message || error?.response?.data?.error || 'We could not start your free trial. Please try again.';
    throw new Error(message);
  }
};

export const setDefaultPaymentMethod = async (paymentMethodId) => {
  try {
    const res = await api.post('/payment-methods/default', { paymentMethodId });
    return res.data;
  } catch (error) {
    console.error('Error setting default payment method:', error.response?.data || error.message);
    throw paymentMethodError(error, 'Failed to update your default card');
  }
};

export const deletePaymentMethod = async (paymentMethodId) => {
  try {
    const res = await api.delete(`/payment-methods/${paymentMethodId}`);
    return res.data;
  } catch (error) {
    console.error('Error removing payment method:', error.response?.data || error.message);
    throw paymentMethodError(error, 'Failed to remove this card');
  }
};

// Edit a saved card's billing details (name / email / address) on Stripe.
// billing = { name, email, address: { line1, line2, city, state, postalCode, country } }
// Updates ONLY the card's billing_details (paymentMethods.update) — not the customer.
export const updatePaymentMethodBilling = async (paymentMethodId, billing) => {
  try {
    const res = await api.put(`/payment-methods/${paymentMethodId}/billing`, billing);
    return res.data;
  } catch (error) {
    console.error('Error updating billing details:', error.response?.data || error.message);
    throw paymentMethodError(error, 'Failed to update billing details');
  }
};

// { canReactivate, mode: 'resume' | 'checkout' | null, nextChargeDate, card, needsCard, ... }
export const getReactivationStatus = async (agentId) => {
  try {
    const res = await api.get(`/reactivate-subscription/${agentId}/status`);
    return res.data;
  } catch (error) {
    console.error('Error loading reactivation status:', error.response?.data || error.message);
    throw paymentMethodError(error, 'Could not check your subscription');
  }
};

// Resolves to { mode: 'resumed' } or { mode: 'checkout', checkoutUrl, clientSecret?, sessionId? }.
// err.code === 'card_required' -> add a card first. embedded: true asks for an in-page (embedded) checkout.
export const reactivateSubscription = async (agentId, { embedded = false } = {}) => {
  try {
    const res = await api.post(`/reactivate-subscription/${agentId}`, { embedded });
    return res.data;
  } catch (error) {
    console.error('Error reactivating subscription:', error.response?.data || error.message);
    const err = paymentMethodError(error, 'Could not reactivate your plan');
    err.code = error?.response?.data?.code || null;
    throw err;
  }
};

export const confirmReactivation = async (agentId, sessionId) => {
  try {
    const res = await api.post(`/reactivate-subscription/${agentId}/confirm`, { sessionId });
    return res.data;
  } catch (error) {
    console.error('Error confirming reactivation:', error.response?.data || error.message);
    const err = paymentMethodError(error, 'Could not confirm your reactivation');
    err.code = error?.response?.data?.code || null;
    throw err;
  }
};
// `deletionReason` / `deletionReasonText` come from the delete screen's
// "Why are you leaving?" step. Sent both as a body and in the query string:
// DELETE bodies are valid but get dropped by some proxies, and the backend
// whitelists the value anyway — losing the feedback must never be able to
// break the deletion itself.
export const deleteUser = async (userId, deletionReason = '', deletionReasonText = '') => {
  try {
    const params = {};
    if (deletionReason) params.deletionReason = deletionReason;
    if (deletionReasonText) params.deletionReasonText = deletionReasonText;

    const res = await api.delete(`/agent/delete-user/${userId}`, {
      // Fresh token, not the module-level `token` captured at import: this route
      // now requires auth, and a tab that loaded the bundle before signing in
      // would send the stale (empty) one and 401.
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token') || ''}`
      },
      params,
      data: params
    });

    return res.data;
  } catch (error) {
    console.error('Error deleting user:', error.response?.data || error.message);
    throw new Error('Failed to delete user');
  }
};
export const saveAgentSchedule = async (scheduleData) => {
  try {
    const res = await api.post('/agent/schedule-agent', scheduleData, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return res.data;
  } catch (error) {
    console.error('Error saving agent schedule:', error.response?.data || error.message);
    throw new Error('Failed to save agent schedule');
  }
};
export const getAgentScheduleByUserId = async (userId) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/agent/agent-schedule/${userId}`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    return res.data;
  } catch (error) {
    console.error('Error fetching agent schedule:', error.response?.data || error.message);
    throw new Error('Failed to fetch agent schedule');
  }
};
export const fetchAvailablePhoneNumberByCountry = async (token, country_code, locality, administrative_area, startsWith, endsWith) => {
  let t = token;
  try {
    const res = await axios.get(`${API_BASE_URL}/telnyx/available-numbers`, {
      params: {
        country_code,
        locality,
        administrative_area,
        starts_with: startsWith,
        ends_with: endsWith
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${t}`
      }
    });

    return res.data;
  } catch (error) {
    return error.response?.data;
  }
};
export const createNumberOrder = async (phoneNumbers, agent_id) => {
  try {
    const res = await axios.post(
      `${API_BASE_URL}/telnyx/create-number-order`,
      { phoneNumbers: phoneNumbers, agent_id: agent_id },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    );
    return res.data;
  } catch (error) {
    console.log(error);
  }
};

// ── Vobiz numbers (India) ────────────────────────────────────────────────────
// Deliberately separate from the two Telnyx functions above rather than making
// those switch provider. Telnyx sells US/CA and Vobiz sells India, so nothing
// chooses between them at runtime except the site — and the Telnyx pair is on
// the live AppSumo path, which is not worth touching for a feature it will
// never use. Callers pick; see usesVobiz() in utils/apiBackend.js.
//
// The Node routers expose the SAME paths and the SAME response shape under
// /api/vobiz as under /api/telnyx, so anything already rendering a Telnyx
// number list renders these without changes.

// Same signature as fetchAvailablePhoneNumberByCountry so a caller can swap one
// for the other. country_code is IN; locality is the city (mapped to an STD
// code server-side, since Vobiz's inventory only carries a state).
export const fetchAvailableVobizNumbers = async (token, country_code, locality, administrative_area, startsWith, endsWith) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/vobiz/available-numbers`, {
      params: {
        country_code,
        locality,
        administrative_area,
        starts_with: startsWith,
        ends_with: endsWith
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    return res.data;
  } catch (error) {
    return error.response?.data;
  }
};

// Buys the number and hands it to the agent. Reads the token fresh rather than
// using the module-level `token`, which is captured at import and is therefore
// empty for a user who signed up in this same page load — exactly the
// onboarding flow that buys the first number.
export const createVobizNumberOrder = async (phoneNumbers, agent_id) => {
  try {
    const res = await axios.post(
      `${API_BASE_URL}/vobiz/create-number-order`,
      { phoneNumbers: phoneNumbers, agent_id: agent_id },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || token}`
        }
      }
    );
    return res.data;
  } catch (error) {
    // Jaan-boojh kar Telnyx ke createNumberOrder ke barabar: purchase failure
    // user ko NAHI dikhani. Error yahin nigal kar `undefined` lautta hai, is
    // liye kisi bhi caller ka `orderRes?.success === false` check fire nahi
    // hota — na throw, na failed popup. Flow aage badh kar number agent pe
    // assign kar deta hai aur "Number assigned successfully" dikhata hai,
    // chahe carrier ne (balance khatam hone par) becha kuch na ho.
    //
    // Failure chhupti nahi, sirf user se chhupti hai: backend ka
    // notifyVobizOrderFailure() PAYMENT alert mail bhej deta hai
    // (NUMBER_ORDER_ALERT_EMAIL → ADMIN_EMAIL → OWNER_EMAIL), Telnyx jaisa hi.
    console.log(error);
  }
};

export const createEvent = async (apiKey, eventName, slug, eventLength) => {
  try {
    const res = await axios.post(`${API_BASE_URL}/agent/createCalEvent`, {
      apiKey: apiKey,
      eventName: eventName,
      slug: slug,
      eventLength: eventLength
    });
    return res.data;
  } catch (error) {
    console.log(error);
  }
};

// `range` ({ startDate, endDate } as ISO date-times) is optional — when given it is
// sent instead of month/year so the export can cover any date range.
// No Authorization here on purpose: the request interceptor attaches the
// CURRENT token. Passing the module-load `token` sent an empty `Bearer ` after a
// login without reload → 401.
export const sendUserAgentCallsByMonth = async (userId, month, year, range) => {
  try {
    const res = await api.get(`/callHistory/user/${userId}/calls-by-month/send-email`, {
      params: range ? range : { month, year },
    });
    return res.data;
  } catch (error) {
    console.error('Error sending user agent calls by month:', error.response?.data || error.message);
    // Backend's own reason (e.g. no calls in the period) → caller shows it.
    if (error.response?.data?.status === false) return error.response.data;
    throw new Error('Failed to send user agent calls by month');
  }
};

export const sendAgentCallsByMonth = async (agentId, month, year, range) => {
  try {
    const res = await api.get(`/callHistory/agent/${agentId}/calls-by-month/send-email`, {
      params: range ? range : { month, year },
    });
    return res.data;
  } catch (error) {
    console.error('Error sending agent calls by month:', error.response?.data || error.message);
    if (error.response?.data?.status === false) return error.response.data;
    throw new Error('Failed to send agent calls by month');
  }
};
// `token` module load par ek hi baar read hota hai — fresh login ke baad usi tab
// mein woh stale/empty ho sakta hai. Knowledge-base file calls yahan se live
// token lete hain taki 401 par upload silently fail na ho.
const liveToken = () => localStorage.getItem('token') || token;

export const uploadAgentFiles = async (agentId, files) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('additional_file', file);
  });

  try {
    const response = await api.post(`/agent/upload-agent-files/${agentId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${liveToken()}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading files:', error);
    throw new Error('Error uploading files');
  }
};

export async function getUserNotifications(userId) {
  try {
    const res = await axios.get(`${API_BASE_URL}/notifications/user/${userId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    return res.data;
  } catch (error) {
    console.error(' Error fetching notifications:', error.response?.data || error.message);
    return (
      error?.response?.data || {
        success: false,
        message: 'Failed to fetch notifications'
      }
    );
  }
}

export const markNotificationAsSeen = async (id, type) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/notifications/${id}/read`,
      { type },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
    return response.data;
  } catch (err) {
    return { success: false, error: err.message };
  }
};

export const getAgentFiles = async (agentId) => {
  try {
    const response = await api.get(`/agent/get-agent-files/${agentId}`, {
      headers: {
        Authorization: `Bearer ${liveToken()}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching agent files:', error);
    throw new Error('Error fetching agent files');
  }
};

export const deleteAgentFile = async (agentId, filename) => {
  try {
    const response = await api.delete(
      `/agent/delete-file/${agentId}/${encodeURIComponent(filename)}`,
      {
        headers: {
          Authorization: `Bearer ${liveToken()}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting agent file:', error.response?.data || error.message);
    throw new Error('Error deleting agent file');
  }
};

export const listSiteMap = async (url) => {
  // Python flow (rexpt.in / port 3001): crawl skip — Python backend khud
  // businessWebsite se crawl + 10-URL select + scrape karta hai. Empty deke
  // callers ka koi crawl-result set nahi hota (sitemap URLs khaali jaate hain).
  if (isPythonBackend()) {
    return { success: true, total: 0, urls: [] };
  }
  try {
    const response = await api.post(
      `/map/list-sitemap2`,
      { url },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting agent file:', error.response?.data || error.message);
    throw new Error('Error deleting agent file');
  }
};

export const classifyBusinessCategoryApi = async (business, allowedCategories) => {
  try {
    const response = await api.post('/maps/classifyBusinessCategory', {
      business,
      allowedCategories,
    });
    return response.data?.category || null;
  } catch (error) {
    console.error('classifyBusinessCategoryApi failed:', error.response?.data || error.message);
    return null;
  }
};

export const sendEmailToOwner = async (email, name, phone) => {
  try {
    const response = await api.post(
      `/endusers/sendEmailToOwner`,
      { email: email, name: name, phone: phone },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting agent file:', error.response?.data || error.message);
    throw new Error('Error deleting agent file');
  }
};
export const customPlanCheck = async (userId) => {
  try {
    let res = axios.get(`${API_BASE_URL}/tier/${userId}`);
    return res;
  } catch (error) {
    console.log(error, 'Error');
    return error;
  }
};

export const getDashboardTourStatus = async (userId) => {
  try {
    const res = await api.get(`/agent/status/${userId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data;
  } catch (error) {
    console.error('Error fetching tour status:', error.response?.data || error.message);
    throw new Error('Failed to fetch tour status');
  }
};

export const markDashboardTourSeen = async (userId) => {
  try {
    const res = await api.post(
      `/agent/seen/${userId}`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return res.data;
  } catch (error) {
    console.error('Error marking tour as seen:', error.response?.data || error.message);
    throw new Error('Failed to mark tour as seen');
  }
};

export const createChatAgent = async (payload, token) => {
  try {
    const res = await api.post(
      `/agent/createChatAgent`,
      { payload },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return res.data;
  } catch (error) {
    console.error('Error marking tour as seen:', error.response);
  }
};
export const updateAgentChatEnabled = async (agent_id, newState, token) => {
  try {
    const res = await api.post(
      `/agent/updateAgentChatEnabled`,
      {
        agent_id: agent_id,
        newState: newState
      },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return res.data;
  } catch (error) {
    console.error('Error marking tour as seen:', error.response);
  }
};
export const updateChatAgent = async (payload, token) => {
  try {
    const res = await api.put(
      `/agent/updateChatAgent`,
      { payload },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return res.data;
  } catch (error) {
    console.error('Error marking tour as seen:', error.response);
  }
};

// Sirf ginti (dashboard ka "Calendar Bookings"). Poori list mangwa kar .length
// lena dheema tha — server ab COUNT(*) karke ek number bhejta hai.
// Fail ho to null: caller "0 bookings" aur "pata nahi" me farq kar sake.
export const getAppointmentsCount = async (userId = null, agentId = null) => {
  try {
    const params = { countOnly: true };
    if (userId) params.userId = userId;
    if (agentId) params.agentId = agentId;
    const res = await api.get('/appointments', { params });
    return typeof res.data?.count === "number" ? res.data.count : null;
  } catch (error) {
    console.error("getAppointmentsCount failed:", error?.response?.status, error?.message);
    return null;
  }
};

export const getAppointments = async (userId = null, agentId = null) => {
  try {
    const params = {};
    if (userId) params.userId = userId;
    if (agentId) params.agentId = agentId;

    const res = await api.get('/appointments', { params });

    return res.data;
  } catch (error) {
    // Chup-chaap undefined return karne se failure aur "0 bookings" ek jaise dikhte the.
    console.error(
      "getAppointments failed:",
      error?.response?.status,
      error?.response?.data || error.message
    );
    return { success: false, data: [] };
  }
};

export const modifyAgentFields = async (agentId, ventryUrl, token) => {
  try {
    const res = await axios.patch(
      `${API_BASE_URL}/agent/modifyAgentFields`,
      {
        agent_id: agentId,
        ventryUrl: ventryUrl
      },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return res.data;
  } catch (error) {
    console.error('Error fetching :', error.response?.data || error.message);
    return { success: false, data: error };
  }
};
export const checkAgentExistence = async (agentCode) => {
  try {
    const res = await api.post('/agent/checkAgentExistence', {
      agent_code: agentCode
    });

    return res.data;
  } catch (error) {
    console.error('Error fetching agent existence:', error.response?.data || error.message);
    return { success: false, data: error };
  }
};
export const getPhoneNumberAccordingToCountry = async (token, country_code) => {
  try {
    const isVobiz =
      usesVobiz() ||
      country_code === "IN" ||
      (isPythonBackend() && !isAppSumoUser());
    // Fetch available numbers (already working)
    const res = isVobiz
      ? await fetchAvailableVobizNumbers(token, "IN", "Bangalore", "KA", null, null)
      : await fetchAvailablePhoneNumberByCountry(token, country_code, '', '');
    const numbers = res?.data;
    if (Array.isArray(numbers) && numbers.length > 0) {
      //  Pick a random number
      const randomIndex = Math.floor(Math.random() * numbers.length);
      const selectedNumber = numbers[randomIndex]?.phone_number;
      if (selectedNumber) {
        //  Save to session storage
        sessionStorage.setItem('assignedPhoneNumber', selectedNumber);

        // You can also set it in React state if needed:
      } else {
        console.warn('No valid phone number found in the list');
      }
    } else {
      console.warn('No available numbers found');
    }
  } catch (error) {
    console.error('Error fetching available phone numbers:', error);
  }
};
export const assignNumberToAgent = async (assignedNumber, agent_id) => {
  try {
    const isVobiz =
      usesVobiz() ||
      (assignedNumber && assignedNumber.startsWith("+91")) ||
      (isPythonBackend() && !isAppSumoUser());
    // 1Create number order
    const response = isVobiz
      ? await createVobizNumberOrder(assignedNumber, agent_id)
      : await createNumberOrder(assignedNumber, agent_id);

    // 2 Update agent with number details
    await updateAgent(agent_id, {
      voip_numbers: [assignedNumber],
      voip_numbers_created: new Date(),
      assignNumFree: 1,
      isNumActivated: 1
    });
  } catch (error) {
    console.error(' Failed to assign number:', error);
  }
};
export const deleteVoipNumber = async (voip_numbers, agent_id) => {
  try {
    // 1Create number order
    const res = await axios.delete(`${API_BASE_URL}/telnyx/deleteNumber`, {
      data: { phoneNumber: voip_numbers },
      headers: { Authorization: `Bearer ${token}` }
    });

    // 2 Update agent with number details
    await updateAgent(agent_id, {
      voip_number_edit_mode: 0
    });
  } catch (error) {
    console.error(' Failed to assign number:', error);
  }
};

export const updateOnBoardingTracker = async (entry) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/onboardingTracker/updateOnboardingTracker`, entry);
    return response.data;
  } catch (error) {
    console.error('Failed to update onboarding tracker:', error);
    throw error;
  }
};

export const getOnBoardingTracker = async ({ userId, email }) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/onBoardingTracker/onboarding-tracker`, {
      params: {
        ...(email ? { email } : {}),
        ...(userId ? { userId } : {})
      }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to get onboarding tracker data', error);
    throw error;
  }
};

export const getDraftAgentTourStatus = async ({ userId, email }) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/onBoardingTracker/draft-tour-status`, {
      params: {
        ...(email ? { email } : {}),
        ...(userId ? { userId } : {})
      }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to get draft tour status', error);
    throw error;
  }
};

export const markDraftAgentTourSeen = async ({ userId, email }) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/onBoardingTracker/draft-tour-seen`, {
      ...(email ? { email } : {}),
      ...(userId ? { userId } : {})
    });
    return response.data; // { success: true, ... }
  } catch (error) {
    console.error('Failed to mark draft tour as seen', error);
    throw error;
  }
};
export const createLlmWithKnowledgBaseId = async (token, data) => {
  try {
    const res = await api.post('/agent/createLlmWithKnowledgBaseId', data, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return res;
  } catch (error) {
    console.log(error);
  }
};
export const createAgentWithLlmAndKnowledgeBaseId = async (token, data) => {
  try {
    const res = await api.post('/agent/createAgentWithLlmAndKnowledgeBaseId', data, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return res;
  } catch (error) {
    console.log(error);
  }
};
export const createKnowledgeBaseWithDetails = async (token, data) => {
  try {
    const res = await api.post('/agent/createKnowledgeBaseWithDetails', data, {
      headers: { Authorization: `Bearer ${token}` }
    });

    return res.data;
  } catch (error) {
    const payload = error?.response?.data;
    console.error('createKnowledgeBaseWithDetails failed:', payload || error?.message);
    return {
      success: false,
      message: payload?.message || error?.message || 'KB create failed',
      error: payload?.error || payload || error?.message
    };
  }
};

export const handleAssignNumberValidtyCheck = async (agentId) => {
  try {
    const res = await axios.post(`${API_BASE_URL}/check-assign-number-month`, {
      agentId
    });
    return res.data;
  } catch (error) {
    console.error('Error checking assign number:', error);
    return false;
  }
};

export const createPhoneNumberAndAssign = async (token, payload) => {
  try {
    const res = await api.post('/agent/create-phone-number', payload, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data;
  } catch (error) {
    const e = error?.response?.data;
    return {
      success: false,
      message: e?.message || error?.message || 'Error assigning number',
      error: e || error?.message
    };
  }
};

export const deleteKnowledgeBase = async (token, knowledgeBaseId) => {
  const res = await axios.delete(`${API_BASE_URL}/agent/delete-knowledge-base/${knowledgeBaseId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

export const updateRetellAgent = async (token, agentId, payload) => {
  try {
    const res = await api.patch(`/agent/update-agent/${agentId}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return res.data;
  } catch (error) {
    const payloadErr = error?.response?.data;
    console.error('updateRetellAgent failed:', payloadErr || error?.message);
    return {
      success: false,
      message: payloadErr?.message || error?.message || 'Agent update failed',
      error: payloadErr?.error || payloadErr || error?.message
    };
  }
};

export const getKnowledgeBase = async (token, knowledgeBaseId) => {
  try {
    const res = await api.get(`/agent/get-knowledge-base/${knowledgeBaseId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data;
  } catch (error) {
    const payload = error?.response?.data;
    console.error('getKnowledgeBase failed:', payload || error?.message);
    return {
      success: false,
      message: payload?.message || error?.message || 'Failed to fetch KB',
      error: payload?.error || payload || error?.message
    };
  }
};

export const addKnowledgeBaseSources = async (token, knowledgeBaseId, formData, agent_id) => {
  try {
    const res = await api.post(`/agent/add-knowledge-base-sources/${knowledgeBaseId}`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      },
      params: {
        agentId: agent_id // ✅ This adds ?agentId=xxx to the URL
      }
    });
    return res.data;
  } catch (error) {
    const payload = error?.response?.data;
    console.error('addKnowledgeBaseSources failed:', payload || error?.message);
    return {
      success: false,
      message: payload?.message || error?.message || 'Failed to add KB sources',
      error: payload?.error || payload || error?.message
    };
  }
};

export const deleteKnowledgeBaseSource = async (token, knowledgeBaseId, sourceId) => {
  try {
    const res = await api.delete(`/agent/delete-knowledge-base-source/${knowledgeBaseId}/source/${sourceId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data;
  } catch (error) {
    const payload = error?.response?.data;
    console.error('deleteKnowledgeBaseSource failed:', payload || error?.message);
    return {
      success: false,
      message: payload?.message || error?.message || 'Failed to delete KB source',
      error: payload?.error || payload || error?.message
    };
  }
};

//  Agent Callers APIs
// Create Caller
export const createAgentCaller = async ({ agentId, name, phone }) => {
  const res = await api.post(
    `/agent-callers`,
    { agentId, name, phone },
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );
  return res.data;
};

// List Callers (
export const listAgentCallers = async (agentId, q = '') => {
  const res = await api.get(`/agent-callers`, {
    params: { agentId, ...(q ? { q } : {}) },
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return res.data;
};

// Update Caller
export const updateAgentCaller = async (id, { agentId, name, phone }) => {
  const res = await api.put(
    `/agent-callers/${id}`,
    { agentId, ...(name !== undefined ? { name } : {}), ...(phone !== undefined ? { phone } : {}) },
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );
  return res.data;
};

// Delete Caller
export const deleteAgentCaller = async (id, agentId) => {
  const res = await api.delete(`/agent-callers/${id}`, {
    params: { agentId },
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return res.data;
};

export default api;

// 1) GET all transfer numbers by agent_id
export const getAgentTransferNumbers = async (agent_id) => {
  if (!agent_id) throw new Error('agent_id is required');
  const res = await api.get(`/agent-transfer-numbers`, {
    params: { agent_id },
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`
    }
  });
  return res.data;
};

// 2) BULK SAVE (Add/Update multiple at once + sync delete missing)
export const bulkSaveAgentTransferNumbers = async (agent_id, numbers = []) => {
  if (!agent_id) throw new Error('agent_id is required');
  const payload = { agent_id, numbers };

  const res = await api.post(`/agent-transfer-numbers/save`, payload, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    }
  });
  return res.data;
};

// 3) DELETE single transfer number by DB row id
export const deleteAgentTransferNumberById = async (id) => {
  if (!id) throw new Error('id is required');
  const res = await api.delete(`/agent-transfer-numbers/${id}`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`
    }
  });
  return res.data;
};

// 4) DELETE ALL transfer numbers by agent_id
export const deleteAllAgentTransferNumbers = async (agent_id) => {
  if (!agent_id) throw new Error('agent_id is required');
  const res = await api.delete(`/agent-transfer-numbers`, {
    params: { agent_id },
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`
    }
  });
  return res.data;
};

// 5) GET agent SMS toggle setting (send_transfer_sms) by agent_id
export const getAgentSmsSetting = async (agent_id) => {
  if (!agent_id) throw new Error('agent_id is required');

  const res = await api.get(`/agent/${agent_id}/sms-setting`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`
    }
  });

  return res.data;
};

// 6) UPDATE agent SMS toggle (true/false)
export const updateAgentSmsSetting = async (agent_id, send_transfer_sms) => {
  if (!agent_id) throw new Error('agent_id is required');
  if (typeof send_transfer_sms !== 'boolean') throw new Error('send_transfer_sms must be boolean');

  const payload = { send_transfer_sms };

  const res = await api.patch(`/agent/${agent_id}/send-transfer-sms`, payload, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    }
  });

  return res.data;
};

export const getBusinessHoursByBusinessId = async (businessId) => {
  const res = await api.get(`/business-hours/${businessId}`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token') || ''}`
    }
  });
  return res.data;
};

export const updateBusinessHoursByBusinessId = async (businessId, hours) => {
  const res = await api.patch(
    `/business-hours/${businessId}`,
    { hours },
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token') || ''}`
      }
    }
  );
  return res.data;
};

export const saveAgentWidgetScript = async (agent_id, widgetScript) => {
  const token = localStorage.getItem('token') || '';
  const res = await fetch(`${API_BASE_URL}/agent/updateWidgetScript`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ agent_id, widgetScript })
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data?.message || 'Failed to save widget script');
  return data;
};
export const updateAgentFromBackend = async (payload) => {
  // console.log(payload,"payload")
  const res = await api.patch(`${API_BASE_URL}/agentV2/updateAgent`, payload);
  return res.data;
};

export const listContacts = async ({ search, category, page = 1, limit = 500 } = {}) => {
  const params = { page, limit };
  if (search) params.search = search;
  if (category && category !== "all") params.source = category;
  const res = await api.get("/contact-routes/contacts", {
    params,
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const createContact = async (data) => {
  const res = await api.post("/contact-routes/contacts", data, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const updateContact = async (id, data) => {
  const res = await api.put(`/contact-routes/contacts/${id}`, data, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const deleteContact = async (id) => {
  const res = await api.delete(`/contact-routes/contacts/${id}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const bulkDeleteContacts = async (ids) => {
  const res = await api.post(`/contact-routes/contacts/bulk-delete`, { ids }, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const bulkUpdateContacts = async (ids, updates) => {
  const res = await api.put(`/contact-routes/contacts/bulk-update`, { ids, updates }, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const blockContact = async (id) => {
  const res = await api.put(`/contact-routes/contacts/${id}/block`, {}, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const unblockContact = async (id) => {
  const res = await api.put(`/contact-routes/contacts/${id}/unblock`, {}, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const uploadContacts = async (formData) => {
  const res = await api.post("/contact-routes/contacts/upload", formData, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
      "Content-Type": "multipart/form-data",
    },
  });
  return res.data;
};


// Blacklist
export const listBlacklist = async (agentId) => {
  const params = {};
  if (agentId) params.agentId = agentId;
  const res = await api.get("/contact-routes/blacklist", {
    params,
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const addToBlacklist = async ({ phone, scope = "user", agentId, notes }) => {
  const res = await api.post("/contact-routes/blacklist", { phone, scope, agentId, notes }, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const removeFromBlacklist = async (id) => {
  const res = await api.delete(`/contact-routes/blacklist/${id}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

// ── Agent Contacts (per-agent tracking, blocking, promote) ───────────────────

export const listAgentContacts = async ({ agentId, isBlocked, watchList, search, page = 1, limit = 50 }) => {
  const params = { page, limit };
  if (agentId) params.agentId = agentId;
  if (isBlocked) params.isBlocked = "true";
  if (watchList) params.watchList = "true";
  if (search) params.search = search;
  const res = await api.get("/contact-routes/agent-contacts", {
    params,
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const getAgentContact = async (id) => {
  const res = await api.get(`/contact-routes/agent-contacts/${id}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const agentContactsSummary = async (agentId) => {
  const params = {};
  if (agentId) params.agentId = agentId;
  const res = await api.get("/contact-routes/agent-contacts/summary", {
    params,
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const blockAgentContact = async (id, reason) => {
  const res = await api.put(`/contact-routes/agent-contacts/${id}/block`, { reason }, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const unblockAgentContact = async (id) => {
  const res = await api.put(`/contact-routes/agent-contacts/${id}/unblock`, {}, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const unblockAllAgents = async (phone) => {
  const res = await api.put("/contact-routes/agent-contacts/unblock-all", { phone }, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const bulkBlockAgentContacts = async (ids, reason = "manual") => {
  const res = await api.put("/contact-routes/agent-contacts/bulk-block", { ids, reason }, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const bulkUnblockAgentContacts = async (ids) => {
  const res = await api.put("/contact-routes/agent-contacts/bulk-unblock", { ids }, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const deleteAgentContact = async (id) => {
  const res = await api.delete(`/contact-routes/agent-contacts/${id}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const bulkDeleteAgentContacts = async (ids) => {
  const res = await api.post("/contact-routes/agent-contacts/bulk-delete", { ids }, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const bulkPromoteAgentContacts = async (ids) => {
  const res = await api.post("/contact-routes/agent-contacts/bulk-promote", { ids }, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const getPromoteInfo = async (id) => {
  const res = await api.get(`/contact-routes/agent-contacts/${id}/promote-info`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const promoteAgentContact = async (id, { name, email, company, category, isVip }) => {
  const res = await api.post(`/contact-routes/agent-contacts/${id}/promote`, { name, email, company, category, isVip }, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

// Contact lookup + spam logging + watch list
export const contactLookup = async (phone, agentId) => {
  const res = await api.post("/contact-routes/contacts/lookup", { phone, agent_id: agentId }, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const logSpam = async ({ phone, reason, agentId, callerName }) => {
  const res = await api.post("/contact-routes/contacts/log-spam", { phone, reason, agent_id: agentId, caller_name: callerName }, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const listWatchList = async (agentId) => {
  const params = {};
  if (agentId) params.agentId = agentId;
  const res = await api.get("/contact-routes/watch-list", {
    params,
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

// Industry templates + dynamic categories
export const getIndustryTemplates = async () => {
  const res = await api.get("/contact-routes/industry-templates", {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const getAgentCategories = async (agentId) => {
  const res = await api.get(`/contact-routes/agent-categories/${agentId}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const updateAgentCategories = async (agentId, { categories, industry }) => {
  const res = await api.put(`/contact-routes/agent-categories/${agentId}`, { categories, industry }, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

// Generic agent settings section get/update
export const getAgentSettingsSection = async (agentId, section) => {
  const res = await api.get(`/contact-routes/agent-settings/${agentId}/${section}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data?.data || res.data;
};

export const updateAgentSettingsSection = async (agentId, section, data) => {
  const res = await api.put(`/contact-routes/agent-settings/${agentId}/${section}`, data, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

// Agent spam settings
export const getAgentSpamSettings = async (agentId) => {
  const res = await api.get(`/contact-routes/agent-settings/${agentId}/spam`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });
  // Backend returns { agentId, section, data: {...} }
  return res.data?.data || res.data || {};
};

// export const updateAgentSpamSettings = async (agentId, settings) => {
//   // Ensure plain object — never pass JSON.stringify result
//   const body = typeof settings === "string"
//     ? JSON.parse(settings)
//     : { ...settings };  // spread to ensure plain object

//   const res = await api.put(`/contact-routes/agent-settings/${agentId}/spam`, body, {
//     headers: {
//       Authorization: `Bearer ${localStorage.getItem("token")}`,
//       "Content-Type": "application/json",
// export const getAgentsByUserIdforkey = async () => {
//   const res = await api.get(`${API_BASE_URL}/endusers/getAgentsByuserId`, {
//     headers: {
//       Authorization: `Bearer ${localStorage.getItem('token') || ''}`
//     }
//   });
//   return res.data;
// };
export const updateAgentSpamSettings = async (agentId, settings) => {
  // Ensure plain object — never pass JSON.stringify result
  const body = typeof settings === "string"
    ? JSON.parse(settings)
    : { ...settings };  // spread to ensure plain object

  const res = await api.put(`/contact-routes/agent-settings/${agentId}/spam`, body, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
      "Content-Type": "application/json",
    },
  });
  return res.data;
};
export const getAgentsByUserIdforkey = async () => {
  const res = await api.get(`${API_BASE_URL}/endusers/getAgentsByuserId`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token') || ''}`
    }
  });
  return res.data;
};
// ========== Team Member APIs ==========
export const getMyTeamInfo = async () => {
  const res = await api.get("/team/my-team-info", {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });
  return res.data;
};

// Multi-owner: all active memberships for the logged-in team member, each with
// that owner's identity + the role/permissions they granted.
export const getMyTeams = async () => {
  const res = await api.get("/team/my-teams", {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  // { isTeamMember, teams: [{ ownerId, ownerCompany, role, permissions }],
  //   self: { ownerId, ownerName, agentCount, available, ... } }
  return res.data;
};

// Abhi chal rahi calls — sirf REQUESTER ke apne agents ki (backend req.user.id
// se filter karta hai). Retell ki API hit hoti hai, isliye timer pe mat chalao.
export const getActiveLiveCalls = async () => {
  const res = await api.get("/agent/live-monitoring/active-calls", {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data; // { status, calls: [...] }
};

// ADDED: SMS opt-out (owner SMS alerts) shown on the My Account page. GET is
// visible to the whole team — the `api` instance's X-Owner-Id header (set by
// activeContext.js on context switch) scopes a team member's request to
// their owner's row automatically. Only the account owner may PATCH it; the
// backend enforces that regardless of what the client sends.
export const getSmsOptOutStatus = async () => {
  const res = await api.get("/endusers/sms-opt-out-status", {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data; // { success, smsOptOut, smsPrefs: { newLead, meetingBooked, transferFailed } }
};

// Ek key ka object bhejo ({ newLead: false }) — server baaki keys chhedta nahi.
export const updateSmsOptOutStatus = async (patch) => {
  const body = typeof patch === "boolean" ? { smsOptOut: patch } : patch;
  const res = await api.patch(
    "/endusers/sms-opt-out-status",
    body,
    { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
  );
  return res.data; // { success, smsOptOut, smsPrefs, message }
};

// Switch the session into one context (own account or an owner's team) and get
// back a re-signed token. The server verifies membership, so this is the ONLY
// correct way to switch — writing a new ownerId into storage alone would leave
// the JWT (and therefore every server-side permission check) on the old one.
export const switchContext = async (ownerId) => {
  const res = await api.post(
    "/team/switch-context",
    { ownerId },
    { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
  );
  return res.data; // { token, context: { ownerId, isSelf, role, permissions } }
};

// Build a team member's dashboard for the ACTIVE context (see the scoping note
// below — this used to aggregate every owner at once).
// Reuses the existing per-owner endpoint (fetchDashboardDetails) so we don't
// duplicate the heavy stats controller. Each returned agent is tagged with its
// owning team's id/company + that owner's permissions, so downstream gating and
// grouping can resolve everything per-agent.
export const fetchTeamDashboard = async () => {
  const token = localStorage.getItem("token") || "";
  const { teams = [] } = await getMyTeams();

  // A member may ALSO own agents of their own (admins can create one). Those
  // live under their own userId — not under any owner — so fetch them as an
  // extra "My Agents" group with full owner rights.
  const selfId = decodeToken(token)?.id || null;
  const sources = [
    ...(selfId
      ? [{
        ownerId: selfId,
        ownerName: null,
        ownerCompany: "My Agents",
        // Their own agents → full rights, no restrictions.
        role: "owner",
        permissions: {
          viewAgents: true,
          editAgents: true,
          dialer: true,
          viewCallLogs: true,
          viewAppointments: true,
          viewContacts: true,
          manageBilling: true,
        },
      }]
      : []),
    ...teams,
  ];

  // Scope to the ACTIVE context. This used to fan out across every owner at
  // once, which made the access picker meaningless: a member who chose "Company
  // X" still got X's, Y's and their own agents grouped together on the very next
  // screen. The picker sets one context; the dashboard has to honour it.
  //
  // A context that isn't in the list (a membership revoked since the token was
  // signed) falls through to the full set rather than rendering an empty
  // dashboard — useTeamPermissions' contextLost check sends them to re-pick.
  //
  // LEGACY SESSIONS ARE EXEMPT. Tokens minted before the access picker existed
  // carry no `contextCount`, so the picker never offers itself to them — scoping
  // those would silently hide every other company from a multi-team member who
  // is still signed in on a 30-day token, with no obvious way back. They keep
  // the old grouped dashboard until they next sign in (or switch from their
  // profile), and only then does one-context-at-a-time begin.
  const isLegacySession = !decodeToken(token)?.contextCount;
  const activeOwnerId = getActiveOwnerId();
  const scoped = isLegacySession
    ? sources
    : sources.filter((s) => s.ownerId === activeOwnerId);
  const selected = scoped.length ? scoped : sources;

  const groups = await Promise.all(
    selected.map(async (team) => {
      let agents = [];
      let total_call = 0;
      let total_chat = 0;
      try {
        const res = await fetchDashboardDetails(team.ownerId, token);
        agents = (res?.agents || []).map((a) => ({
          ...a,
          __ownerId: team.ownerId,
          __ownerName: team.ownerName || null,
          // Prefer the business name for the grouped-dashboard header — personal
          // names collide and don't tell the member which company it is.
          __ownerCompany: team.ownerBusiness || team.ownerCompany || team.ownerName || null,
          __role: team.role || "support",
          __permissions: team.permissions || {},
        }));
        total_call = res?.total_call || 0;
        total_chat = res?.total_chat || 0;
      } catch (err) {
        console.error("fetchTeamDashboard: owner fetch failed", team.ownerId, err?.message);
      }
      return { ...team, agents, total_call, total_chat };
    })
  );

  const allAgents = groups.flatMap((g) => g.agents);
  const total_call = groups.reduce((s, g) => s + (g.total_call || 0), 0);
  const total_chat = groups.reduce((s, g) => s + (g.total_chat || 0), 0);

  return { teams, groups, agents: allAgents, total_call, total_chat };
};

// User settings
export const updateForwardPhone = async (forwardPhone) => {
  const res = await api.put("/contact-routes/user/forward-phone", { forwardPhone }, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const updateContactRouting = async (enabled) => {
  const res = await api.put("/contact-routes/user/contact-routing", { enabled }, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const updateContactSyncPermission = async (permission) => {
  const res = await api.put("/contact-routes/user/contact-sync-permission", { permission }, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

// Google OAuth URL
// export const getGoogleAuthUrl = async () => {
//   const res = await api.get("/contacts/sync/google/auth", {
//     headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
//   });
//   return res.data;
// };

export const getGoogleSyncStatus = async ({ isMobile = false, flow = "contacts", returnTo } = {}) => {
  // platform forwarded so backend can stamp it on EndUser.platform via
  // the eventual OAuth callback (same way SignUp's Google flow does it).
  const params = { isMobile, flow, platform: detectPlatform() };
  if (returnTo) params.returnTo = returnTo;
  const res = await api.get("/contact-routes/sync/google/status", {
    params,
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

// Keep old name as alias for any existing usage
export const getGoogleAuthUrl = getGoogleSyncStatus;

export const triggerGoogleContactsSync = async () => {
  const res = await api.post(
    "/contact-routes/sync/google/trigger",
    {},
    { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
  );
  return res.data;
};

export const syncNativeContacts = async (contacts) => {
  const res = await api.post(
    "/contact-routes/contacts/sync/native",
    { contacts },
    { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
  );
  return res.data;
};
export const isSyncContactSkip = async () => {
  const res = await api.put(
    "/endusers/updateSyncContactSkip",
    {},
    { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
  );
  return res.data;
};

// ── Agent availability & scheduling (public booking) ──
export const getAgentAvailability = async (agentId) => {
  const res = await api.get("/availability", {
    params: { agent_id: agentId },
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

export const updateAgentAvailability = async (payload) => {
  const res = await api.put("/availability", payload, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

// ── AppSumo code redemption ──
export const previewAppSumoCode = async (code, utmCampaign) => {
  const res = await api.get("/appsumo/preview", {
    params: { code, utm_campaign: utmCampaign },
  });
  return res.data;
};

export const redeemAppSumoCode = async (code, utmCampaign) => {
  const res = await api.post(
    "/appsumo/redeem",
    { code, utm_campaign: utmCampaign },
    { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
  );
  return res.data;
};

export const getAppSumoStatus = async () => {
  const res = await api.get("/appsumo/status", {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

// The Referrals & Earnings screen: this partner's link, everyone who signed up
// through it, and what each is worth per month.
export const getAppSumoReferrals = async () => {
  const res = await api.get("/appsumo/referrals", {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

// Every review platform, what it pays, and where this account stands on each:
// available | pending | approved | rejected.
export const getAppSumoFeedback = async () => {
  const res = await api.get("/appsumo/feedback", {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res.data;
};

// Submit a screenshot of a published review. Records a claim; the minutes land
// only once an admin has approved it.
export const submitAppSumoFeedback = async (platform, file) => {
  const form = new FormData();
  form.append("platform", platform);
  form.append("feedbackScreenshot", file);

  const res = await api.post("/appsumo/feedback", form, {
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });
  return res.data;
};



// Read the token at call time — the module-level `token` is captured at import and goes stale after login.
const sipAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('token') || ''}`
});

// Shared error shape so screens can just read `.success` / `.message`.
const sipError = (error, fallback) => {
  const e = error?.response?.data;
  return {
    success: false,
    message: e?.message || error?.message || fallback,
    error: e?.error || e || error?.message
  };
};

export const getSipSetupInfo = async () => {
  try {
    const res = await api.get('/sip/setup-info', { headers: sipAuthHeader() });
    return res.data;
  } catch (error) {
    return sipError(error, 'Failed to load SIP setup info');
  }
};

export const getSipNumbers = async (userId) => {
  try {
    const res = await api.get(`/sip/numbers/${userId}`, { headers: sipAuthHeader() });
    return res.data;
  } catch (error) {
    return sipError(error, 'Failed to load imported numbers');
  }
};

// payload: { phone_number, termination_uri, sip_username, sip_password, transport, nickname, provider_label, agent_id? }
export const importSipNumber = async (payload) => {
  try {
    const res = await api.post('/sip/import-number', payload, { headers: sipAuthHeader() });
    return res.data;
  } catch (error) {
    return sipError(error, 'Failed to import number');
  }
};

export const assignSipNumber = async (phone_number, agent_id) => {
  try {
    const res = await api.post(
      '/sip/assign-number',
      { phone_number, agent_id },
      { headers: sipAuthHeader() }
    );
    return res.data;
  } catch (error) {
    return sipError(error, 'Failed to assign number');
  }
};

export const unassignSipNumber = async (phone_number) => {
  try {
    const res = await api.post(
      '/sip/unassign-number',
      { phone_number },
      { headers: sipAuthHeader() }
    );
    return res.data;
  } catch (error) {
    return sipError(error, 'Failed to unassign number');
  }
};

export const updateSipNumber = async (payload) => {
  try {
    const res = await api.patch('/sip/number', payload, { headers: sipAuthHeader() });
    return res.data;
  } catch (error) {
    return sipError(error, 'Failed to update number');
  }
};

export const deleteSipNumber = async (phone_number) => {
  try {
    const res = await api.delete('/sip/number', {
      data: { phone_number },
      headers: sipAuthHeader()
    });
    return res.data;
  } catch (error) {
    return sipError(error, 'Failed to remove number');
  }
};
